import React, { useEffect } from 'react';
import { TestCase, Component, Feature } from '../../types';
import { theme } from '../../theme';
import { formatDate } from '../../utils/date';

interface TestCaseReviewModalProps {
  testCase: TestCase;
  feature?: Feature;
  component?: Component;
  onClose: () => void;
  onEdit?: (testCase: TestCase) => void;
  onDelete?: (testCaseId: string) => void;
  zIndex?: number;
  projectCode?: string; // Project code prefix for TC ID display (e.g., "FS" -> "FS-TC-42")
}

const TestCaseReviewModal: React.FC<TestCaseReviewModalProps> = ({
  testCase,
  feature,
  component,
  onClose,
  onEdit,
  projectCode,
  onDelete,
  zIndex = 1000
}) => {
  const hasSteps = testCase.steps && testCase.steps.length > 0;
  const hasPreConditions = testCase.preConditions && testCase.preConditions.trim();
  const hasDescription = testCase.description && testCase.description.trim();

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this test case? This action cannot be undone.')) {
      onDelete?.(testCase.id);
      onClose();
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        zIndex: zIndex,
        padding: '2rem',
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="card"
        style={{
          width: '900px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: zIndex + 1,
          position: 'relative',
          backgroundColor: theme.surface,
          border: `1px solid ${theme.cyanGlow}`,
          boxShadow: `0 0 30px rgba(0, 212, 255, 0.15)`,
          padding: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem 2rem',
          borderBottom: `1px solid ${theme.gridLine}`,
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, transparent 100%)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                marginBottom: '0.5rem'
              }}>
                <span style={{ 
                  width: '36px', 
                  height: '36px', 
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--primary-color) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem'
                }}>
                  📋
                </span>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '1.5rem', 
                  color: theme.cyanGlow,
                  fontWeight: 600
                }}>
                  Test Case Review
                </h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {testCase.testCaseNumber && (
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      padding: '0.25rem 0.5rem',
                      backgroundColor: theme.cyanGlow,
                      color: theme.surface,
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      flexShrink: 0
                    }}
                  >
                    {projectCode ? `${projectCode}-TC-${testCase.testCaseNumber}` : `TC-${testCase.testCaseNumber}`}
                  </span>
                )}
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.125rem', 
                  color: theme.textPrimary,
                  fontWeight: 500
                }}>
                  {testCase.title}
                </h3>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {onEdit && (
                <button
                  className="btn btn-secondary"
                  onClick={() => onEdit(testCase)}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  ✏️ Edit
                </button>
              )}
              {onDelete && (
                <button
                  className="btn btn-secondary"
                  onClick={handleDelete}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: 'var(--danger)' }}
                >
                  🗑️ Delete
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={onClose}
                style={{ padding: '0.5rem 0.75rem', fontSize: '1rem' }}
              >
                ×
              </button>
            </div>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem 2rem'
        }}>
          {/* Meta Information */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            {feature && (
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  Feature
                </div>
                <div style={{ color: 'var(--cyan-glow)', fontWeight: 500 }}>
                  {feature.name}
                </div>
              </div>
            )}
            {component && (
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  Component
                </div>
                <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                  {component.name}
                </div>
              </div>
            )}
            <div>
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.25rem'
              }}>
                Status
              </div>
              <div>
                <span style={{ 
                  padding: '0.25rem 0.5rem',
                  background: testCase.status === 'Active' ? 'var(--success-transparent, rgba(34, 197, 94, 0.1))' 
                    : testCase.status === 'Draft' ? 'var(--cyan-transparent)' 
                    : 'var(--danger-transparent, rgba(239, 68, 68, 0.1))',
                  color: testCase.status === 'Active' ? 'var(--success, #22c55e)' 
                    : testCase.status === 'Draft' ? 'var(--cyan-glow)' 
                    : 'var(--danger)',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  border: `1px solid ${testCase.status === 'Active' ? 'var(--success, #22c55e)' 
                    : testCase.status === 'Draft' ? 'var(--cyan-glow)' 
                    : 'var(--danger)'}`
                }}>
                  {testCase.status || 'Active'}
                </span>
              </div>
            </div>
            <div>
              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--text-muted)', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.25rem'
              }}>
                Automation
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {testCase.automated ? (
                  <span style={{ 
                    padding: '0.25rem 0.5rem',
                    background: 'var(--success)',
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    ✓ Automated
                  </span>
                ) : testCase.automationCandidate ? (
                  <span style={{ 
                    padding: '0.25rem 0.5rem',
                    background: 'var(--warning)',
                    color: 'var(--surface)',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    🎯 Ready to Automate
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Manual
                  </span>
                )}
              </div>
            </div>
            {testCase.createdAt && (
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  Created
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formatDate(testCase.createdAt)}
                </div>
              </div>
            )}
            {testCase.updatedAt && testCase.updatedAt !== testCase.createdAt && (
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  Last Updated
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {formatDate(testCase.updatedAt)}
                </div>
              </div>
            )}
            {hasSteps && (
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.25rem'
                }}>
                  Steps
                </div>
                <div style={{ 
                  color: 'var(--cyan-glow)', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    background: 'var(--cyan-glow)',
                    color: 'var(--surface)',
                    borderRadius: '10px',
                    fontSize: '0.75rem'
                  }}>
                    {testCase.steps!.length}
                  </span>
                  steps defined
                </div>
              </div>
            )}
          </div>

          {/* Description & Pre-Conditions */}
          {(hasDescription || hasPreConditions) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: hasDescription && hasPreConditions ? '1fr 1fr' : '1fr',
              gap: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              {hasDescription && (
                <div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 600,
                    color: 'var(--text-secondary)', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.75rem'
                  }}>
                    Description
                  </div>
                  <div style={{ 
                    padding: '1rem',
                    background: 'var(--surface-elevated)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {testCase.description}
                  </div>
                </div>
              )}
              {hasPreConditions && (
                <div>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 600,
                    color: 'var(--text-secondary)', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.75rem'
                  }}>
                    Pre-Conditions
                  </div>
                  <div style={{ 
                    padding: '1rem',
                    background: 'rgba(255, 193, 7, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    color: 'var(--text-primary)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    ⚠️ {testCase.preConditions}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Test Steps */}
          {hasSteps && (
            <div>
              <div style={{ 
                fontSize: '0.875rem', 
                fontWeight: 600,
                color: 'var(--text-secondary)', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.75rem'
              }}>
                Test Steps
              </div>
              <div style={{ 
                border: '1px solid var(--border-color)', 
                borderRadius: '8px', 
                overflow: 'hidden',
                backgroundColor: 'var(--surface-elevated)'
              }}>
                {/* Steps header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '50px 1.5fr 1fr 1fr',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(0, 212, 255, 0.05)',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div>Step</div>
                  <div>Action</div>
                  <div>Data</div>
                  <div>Expected Result</div>
                </div>

                {/* Steps list */}
                <div>
                  {testCase.steps!.map((step, index) => (
                    <div 
                      key={step.id} 
                      style={{ 
                        display: 'grid',
                        gridTemplateColumns: '50px 1.5fr 1fr 1fr',
                        gap: '1rem',
                        padding: '1rem',
                        alignItems: 'flex-start',
                        borderBottom: index < testCase.steps!.length - 1 ? '1px solid var(--border-color)' : 'none',
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {/* Step number */}
                      <div style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--primary-color) 100%)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {index + 1}
                      </div>
                      
                      {/* Action */}
                      <div style={{ 
                        color: 'var(--text-primary)',
                        lineHeight: 1.5
                      }}>
                        {step.action || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No action defined</span>}
                      </div>

                      {/* Data */}
                      <div style={{ 
                        color: step.data ? 'var(--warning)' : 'var(--text-muted)',
                        lineHeight: 1.5,
                        fontFamily: step.data ? 'monospace' : 'inherit',
                        fontSize: step.data ? '0.875rem' : 'inherit'
                      }}>
                        {step.data || '—'}
                      </div>
                      
                      {/* Expected Result */}
                      <div style={{ 
                        color: step.expectedResult ? 'var(--success)' : 'var(--text-muted)',
                        lineHeight: 1.5
                      }}>
                        {step.expectedResult || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Comments Section */}
          {testCase.comments && testCase.comments.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ 
                fontSize: '0.875rem', 
                fontWeight: 600,
                color: 'var(--text-secondary)', 
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                💬 Comments
                <span style={{ 
                  padding: '0.125rem 0.5rem',
                  background: 'var(--cyan-glow)',
                  color: 'var(--surface)',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {testCase.comments.length}
                </span>
              </div>
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {testCase.comments.map((comment, index) => (
                  <div key={comment.id} style={{
                    padding: '1rem',
                    backgroundColor: index % 2 === 0 ? 'var(--surface-elevated)' : 'var(--surface)',
                    borderBottom: index < testCase.comments!.length - 1 ? '1px solid var(--border-color)' : 'none'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--cyan-glow)' }}>
                        {comment.userName}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: 'var(--text-primary)',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {comment.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state if no content */}
          {!hasDescription && !hasPreConditions && !hasSteps && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem',
              color: 'var(--text-muted)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📋</div>
              <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                No additional details defined
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                This test case only has a title. Edit it to add description, pre-conditions, or steps.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 2rem',
          borderTop: `1px solid ${theme.gridLine}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          background: 'var(--surface-elevated)'
        }}>
          <button
            className="btn btn-primary"
            onClick={onClose}
            style={{ padding: '0.75rem 2rem' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestCaseReviewModal;
